import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Navigate, Route, Routes } from "react-router-dom";
import { storefrontRoutes } from "./routes/storefrontRoutes";
import { tbsRoutes } from "./routes/tbsRoutes";
import { TBS_ONLY } from "./config";
import { NAV_TEXTS } from "./pages/tbs/shared/constants";
import usePageTracking from "./hooks/usePageTracking";
import useAnalyticsUser from "./hooks/useAnalyticsUser";

import PageLoader from "./components/PageLoader/PageLoader";
import NotFoundPage from "./pages/NotFoundPage/NotFoundPage";
import TbsNotFoundPage from "./pages/tbs/TbsNotFoundPage/TbsNotFoundPage";
import { CartDrawerProvider } from "./components/CartComponent/CartDrawerProvider";
import CookieBanner from "./components/CookieConsent/CookieBanner";

// The router, and which of the two applications in this repository this build is.
//
// **One bundle, two products** — the storefront, and the Tiendas Biai management app. Each half is
// a function that returns its routes or `null`, and each is turned off by a flag: `TBS_ENABLED`
// unset removes the till from a storefront, `TBS_ONLY` set removes the shop from a till (`TBS-78`).
// Nothing else in the tree needs to know which build it is in.
//
// This is a bridge, and `TBS-64` is the far side of it: TBS moves to its own workspace with its own
// router, both flags go, and the separation stops being a runtime condition.

export const App: React.FC = () => {
  const { isLoading } = useAuth0();

  // Order matters and is not incidental: both effects unblock on the same `isLoading` flip and
  // flush in the same commit, in call order, so the identity is set before the page view that
  // should carry it (EB-558).
  //
  // Left running in a `TBS_ONLY` build, where they do nothing: GA4 needs both a measurement ID and
  // a consent decision, the app build sets no ID (CI asserts it), and `gaEvent` is a no-op without
  // one. Skipping them by flag would be a second rule about analytics for somebody to keep in step
  // with the first.
  useAnalyticsUser();
  usePageTracking();

  // The shell is the storefront's, so a management build's browser tab reads "Entorno Biai |
  // Reparación de Celulares, Tecnología y Educación" — the shop's title over a POS, which is the
  // thing this ticket is about. Set from here because `public/index.html` is one file serving two
  // builds; **the favicon, the meta description and the JSON-LD in it are still the shop's**, and
  // they stay that way until `TBS-64` gives the app its own shell.
  React.useEffect(() => {
    if (TBS_ONLY) document.title = NAV_TEXTS.brand;
  }, []);

  if (isLoading) {
    return <PageLoader />;
  }

  const routes = (
    <Routes>
      {storefrontRoutes()}

      {/* Tiendas Biai. Returns null unless REACT_APP_TBS_ENABLED is set, so on every storefront
          deployment these routes do not exist and /gestion/* falls through to the 404 below. */}
      {tbsRoutes()}

      {/* `/` belongs to the shop's home page, and it is gone from a management build — so the app
          lands on its own front door instead. A redirect rather than rendering the dashboard here:
          two paths showing one screen is how a bookmark and a link start disagreeing. */}
      {TBS_ONLY && <Route path="/" element={<Navigate to="/gestion" replace />} />}

      {/* The 404 differs by build, because the storefront's renders `HeaderBar` — a cart, a catalog
          search, and a `useCartDrawer()` call that throws without the provider below. */}
      <Route path="*" element={TBS_ONLY ? <TbsNotFoundPage /> : <NotFoundPage />} />
    </Routes>
  );

  if (TBS_ONLY) {
    // Neither wrapper is mounted here. `CartDrawerProvider` is storefront state, and `CookieBanner`
    // asks consent for analytics this build never loads — a POS that opens by asking a cashier
    // about cookies is asking on behalf of nothing.
    return routes;
  }

  return (
    <CartDrawerProvider>
      {routes}

      {/* Outside `Routes` so the prompt is not tied to a page: it has to be answerable from
          wherever the shopper landed, including a shared product link (EB-559). */}
      <CookieBanner />
    </CartDrawerProvider>
  );
};
